// schemaFieldVisibility.js — shared rules for schema field display flags

/**
 * isSchemaFieldHiddenFromDisplay
 *
 * True when a field should be omitted from customer-facing forms / peek
 * (unless the caller explicitly includes hidden fields).
 *
 * Admin "Visible" writes `visible`; forms historically filtered on `hidden`.
 * Treat either flag as authoritative so both stay usable.
 *
 * @param {object|null|undefined} field - Schema field definition.
 * @returns {boolean} Whether the field is hidden from display.
 */
export function isSchemaFieldHiddenFromDisplay(field) {
  if (!field || typeof field !== 'object') return true;
  if (field.hidden === true) return true;
  if (field.visible === false) return true;
  return false;
}

/**
 * isSchemaFieldShownInDefaultLists
 *
 * True when a field should appear on catch-all / unconstrained list views.
 * Default is off so collection lists stay title-first until a field is opted in.
 *
 * @param {object|null|undefined} field - Schema field definition.
 * @returns {boolean} Whether the field belongs in a default list.
 */
export function isSchemaFieldShownInDefaultLists(field) {
  if (!field || typeof field !== 'object') return false;
  return field.displayInDefaultLists === true;
}

function isClickUpManagedType(typeDef = null) {
  const typeKey = String(typeDef?.key || typeDef?.nativeTypeKey || '').trim().toLowerCase();
  const connector = String(typeDef?.connectorKey || '').trim().toLowerCase();
  if (connector === 'clickup') return true;
  if (typeKey.startsWith('clickup_')) return true;
  return false;
}

function isAirtableManagedType(typeDef = null) {
  const typeKey = String(typeDef?.key || typeDef?.nativeTypeKey || '').trim().toLowerCase();
  const connector = String(typeDef?.connectorKey || '').trim().toLowerCase();
  if (connector === 'airtable') return true;
  if (typeKey.startsWith('airtable_')) return true;
  return false;
}

function isNotionManagedType(typeDef = null) {
  const typeKey = String(typeDef?.key || typeDef?.nativeTypeKey || '').trim().toLowerCase();
  const connector = String(typeDef?.connectorKey || '').trim().toLowerCase();
  if (connector === 'notion') return true;
  if (typeKey === 'notion_page' || typeKey.startsWith('notion_db_')) return true;
  return false;
}

/**
 * ClickUp task `name` maps to Appdeck `node.title` (Peek header).
 * Never show a duplicate Name field on ClickUp-managed types.
 *
 * @param {object|null|undefined} field
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function isClickUpTitleMappedField(field, typeDef = null) {
  const fieldName = String(field?.name || field?.key || '').trim().toLowerCase();
  if (fieldName !== 'name') return false;
  return isClickUpManagedType(typeDef);
}

/**
 * Airtable primary field maps to Appdeck `node.title` (Peek header).
 * Never show a duplicate Title schema field on Airtable-managed types.
 *
 * @param {object|null|undefined} field
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function isAirtableTitleMappedField(field, typeDef = null) {
  const fieldName = String(field?.name || field?.key || '').trim().toLowerCase();
  if (fieldName !== 'title') return false;
  return isAirtableManagedType(typeDef);
}

/**
 * Notion title property maps to Appdeck `node.title` (Peek header).
 *
 * @param {object|null|undefined} field
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function isNotionTitleMappedField(field, typeDef = null) {
  const fieldName = String(field?.name || field?.key || '').trim().toLowerCase();
  if (fieldName !== 'title') return false;
  return isNotionManagedType(typeDef);
}

/**
 * Origin primary/name fields that are owned by NodeTitleField.
 *
 * @param {object|null|undefined} field
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function isOriginTitleMappedField(field, typeDef = null) {
  return isClickUpTitleMappedField(field, typeDef)
    || isAirtableTitleMappedField(field, typeDef)
    || isNotionTitleMappedField(field, typeDef);
}

/**
 * ClickUp Assignees are origin-identity only (shown below the form).
 * Never render as an Appdeck people field on ClickUp-managed types.
 *
 * @param {object|null|undefined} field
 * @param {object|null|undefined} typeDef
 * @returns {boolean}
 */
export function isClickUpOriginDetailsField(field, typeDef = null) {
  const fieldName = String(field?.name || field?.key || '').trim().toLowerCase();
  if (fieldName !== 'assignees') return false;
  return isClickUpManagedType(typeDef);
}

/**
 * buildVisibilityPatch
 *
 * Keeps `visible` and `hidden` in sync when either Admin control changes.
 *
 * @param {boolean} nextVisible - Desired Visible checkbox value.
 * @returns {{ visible: boolean, hidden: boolean }} Patch for the field.
 */
export function buildVisibilityPatch(nextVisible) {
  const visible = nextVisible !== false;
  return {
    visible,
    hidden: !visible,
  };
}
